import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import { UserSessionService } from '@com-delta-idp/mnx-oauth';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataLayer } from '../../common/datalayer/datalayer';
import { ApplicationConfig } from '../../config/config';
import { TrackerStaticText } from '../../header/login/login.interface';
import { AccountSummaryService } from '../../services/accountsummary/account-summary.service';
import { LogoutService } from '../../services/logout/logout.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'ngc-accountSummary',
  templateUrl: './account-summary.component.html',
  styleUrls: ['./account-summary.scss']
})
export class AccountSummaryComponent implements OnInit, OnDestroy {
  @Input() resAcctSumContent: any;
  @Input() isDelta: any;
  @Input() staticTrackerAemContent: TrackerStaticText;
  accountSummaryServiceSubscribe: Subscription;
  logOutServiceSubscribe: Subscription;
  meddiallionMessage: any;
  tier360MedallionLabel = 'Delta 360° ™';
  public resAcctSumDetails: any;
  public getMemberDesc: any;
  public memberDesc: any;
  public isSkyMiles: boolean;
  public greetingMessage: string;
  public mydeltaURL = this.appConfig.get('APP_SESS_CUSTOMER_MYDELTA_URL');
  private unsubscribe: Subject<void> = new Subject();
  public showStaticTracker: boolean;

  constructor(
    public bsModalRef: BsModalRef,
    private accountSummaryService: AccountSummaryService,
    private appConfig: ApplicationConfig,
    private logOutService: LogoutService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private datalayer: DataLayer,
    private userSession: UserSessionService,
    private loggingService: LoggingService
  ) {}

  ngOnInit() {
    this.showStaticTracker =
      this.staticTrackerAemContent?.showStaticTracker === 'true' ? true : false;

    if (isPlatformBrowser(this.platformId)) {
      if (this.userSession.getServiceWorkerCheck()) {
        this.getAccountSummary();
      }
    }
  }

  getAccountSummary() {
    this.accountSummaryServiceSubscribe = this.accountSummaryService
      .getAccountSummaryDetails()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (res) => {
          this.resAcctSumDetails = res;
          if (this.resAcctSumDetails) {
            if (this.resAcctSumDetails.membershipLevel === 'FF') {
              if (this.resAcctSumContent) {
                this.getMemberDesc = this.resAcctSumContent.skymilesMember;
              }
              this.isSkyMiles = true;
            } else {
              this.getMemberDesc = this.resAcctSumDetails.is360Tier
                ? this.getTier360MedallionMessage()
                : this.resAcctSumDetails.medallionMemberDesc + '\u00AE';
              this.isSkyMiles = false;
            }
            this.memberDesc = this.getMemberDescription(
              this.resAcctSumDetails.membershipLevel
            );
            this.getLogoUrl(this.resAcctSumDetails);
            if (!this.isDelta) {
              this.meddiallionMessage = this.getTierClassNames(
                this.resAcctSumDetails.membershipLevel
              );
            }
          }
          this.greetingMessage = this.getWelcomeMessage();
        },
        (err) => {
          this.loggingService.log('Caught Error: ' + err);
        }
      );
  }

  getClassNames(medFlag: string, isTier360: boolean, isMillionMiler: boolean) {
    const classList = {
      GM: {
        'account-summary': true,
        'account-summary-gm': true
      },
      DM: {
        'account-summary': true,
        'account-summary-dm': true
      },
      DM360: {
        'account-summary': true,
        'account-summary-dm-360': true
      },
      PM: {
        'account-summary': true,
        'account-summary-pm': true
      },
      FF: {
        'account-summary': true,
        'account-summary-ff ': true
      },
      FO: {
        'account-summary': true,
        'account-summary-sm': true
      },
      MM: {
        'account-summary': true,
        'account-summary-mm': true
      }
    };

    /** Need to check - Duplicate condition check for case 'GM' */
    // else if (medFlag === 'GM') {
    //   bgClassNames = {
    //     'account-summary': true,
    //     'account-summary-vsgm': true
    //   };
    // }

    const bgClassNames = classList[
      this.getMedallionFlag(medFlag, isTier360, isMillionMiler)
    ] ?? {
      'account-summary': true,
      'account-summary-sky-mem': true
    };
    return bgClassNames;
  }

  getMedallionFlag(
    medFlag: string,
    isTier360: boolean,
    isMillionMiler: boolean
  ): string {
    if (isTier360) {
      return 'DM360';
    }

    if (isMillionMiler) {
      return 'MM';
    }

    return medFlag;
  }

  getCurrentClassName(
    medFlag: string,
    isTier360: boolean,
    isMillionMiler: boolean
  ) {
    const object = this.getClassNames(medFlag, isTier360, isMillionMiler);
    const keys = Object.keys(object);
    const last = keys[keys.length - 1];
    return last;
  }

  getLogoUrl(accountSummary) {
    const level = accountSummary.millionMilerTier
      ? parseInt(accountSummary.millionMilerTier, 10).toString()
      : '0';

    const url = accountSummary.is360Tier
      ? `var(--medallion-banner-360-${level})`
      : `var(--medallion-banner-${level})`;

    document.documentElement.style.setProperty('--background', url);
  }

  getTier360MedallionMessage(): string {
    return (
      this.resAcctSumContent.medallion.find(
        (element) => element.medallionId === 'DM360'
      )?.medallionLabel ?? this.tier360MedallionLabel
    );
  }

  getTierClassNames(medFlag: string) {
    let meddiallionMessage = '';
    if (this.resAcctSumContent) {
      if (this.resAcctSumContent?.medallion?.length > 0) {
        for (let i = 0; i < this.resAcctSumContent.medallion.length; i++) {
          if (this.resAcctSumContent.medallion[i].medallionId === medFlag) {
            meddiallionMessage =
              this.resAcctSumContent.medallion[i].medallionMessage;
          }
        }
      }
    }
    return ' ' + meddiallionMessage;
  }

  getMemberDescription(medFlag: string) {
    let member = '';
    if (medFlag === 'FF') {
      member = ' Red';
    } else if (medFlag === 'FO') {
      member = ' Silver';
    } else if (medFlag === 'GM') {
      member = ' Gold';
    }
    return member;
  }

  close() {
    const el = document.getElementsByClassName('toggle-up')[0];
    if (el !== null && el !== undefined) {
      el.className = 'toggle-flyout';
    }
    this.bsModalRef.hide();
    document.getElementsByTagName('html')[0].classList.remove('no-scroll');
  }

  getWelcomeMessage() {
    const date = new Date();
    const hrs = date.getHours();
    let welcomeMessage: string;
    if (
      this.resAcctSumContent !== undefined &&
      this.resAcctSumContent !== null
    ) {
      if (
        this.isDelta &&
        this.resAcctSumContent.salutation &&
        this.resAcctSumContent.salutation.length > 2
      ) {
        if (hrs < 12) {
          welcomeMessage = this.resAcctSumContent.salutation[0].salutationLabel;
        } else if (hrs >= 12 && hrs <= 17) {
          welcomeMessage = this.resAcctSumContent.salutation[1].salutationLabel;
        } else if (hrs >= 17 && hrs <= 24) {
          welcomeMessage = this.resAcctSumContent.salutation[2].salutationLabel;
        }
      } else if (
        !this.isDelta &&
        this.resAcctSumContent.salutation &&
        this.resAcctSumContent.salutation.length > 2
      ) {
        welcomeMessage = this.resAcctSumContent.salutation[3].salutationLabel;
      }
    }
    return welcomeMessage;
  }

  getCloseButtonClassName() {
    let closeBtnClass = '';
    if (this.resAcctSumDetails === undefined) {
      closeBtnClass = 'account-summary-no-data-close-icon';
    }
    return closeBtnClass;
  }

  ngOnDestroy() {
    if (this.accountSummaryServiceSubscribe) {
      this.accountSummaryServiceSubscribe.unsubscribe();
    }
    if (this.logOutServiceSubscribe) {
      this.logOutServiceSubscribe.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribe.unsubscribe();
  }

  getTierStyles(medFlag: string) {
    let bgClassNames;
    if (medFlag === 'FF') {
      bgClassNames = {
        'tier-member-rm': true
      };
    } else if (medFlag === 'FO') {
      bgClassNames = {
        'tier-member-sm': true
      };
    } else if (medFlag === 'GM') {
      bgClassNames = {
        'tier-member-gm': true
      };
    } else {
      bgClassNames = {
        'tier-member-gm': true
      };
    }
    return bgClassNames;
  }

  logOut() {
    if (isPlatformBrowser(this.platformId) && document) {
      let logoutUrl = '';
      if (
        this.appConfig.get('APP_LOGOUT_URL_FROM_PROPERTY') === 'true' &&
        this.appConfig.get('APP_NEW_LOGOUT_URL') !== undefined
      ) {
        logoutUrl = this.appConfig.get('APP_NEW_LOGOUT_URL');
      } else {
        logoutUrl = this.resAcctSumContent.logoutUrl;
      }
      this.logOutServiceSubscribe = this.logOutService
        .logOutAction(logoutUrl)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (res) => {
            this.logOutService.pingLogOut();
            //window.location.replace(this.urlUtil.hostUrl);
          },
          (err) => {
            window.location.replace('/');
            this.loggingService.log('Caught Error: ' + err);
          }
        );
    }
  }
}
